import { FC } from 'react';
import IconWrapper, { IIcon, IIconColor } from './IconWrapper';

// Creates a type for each record in the icons object. The keys a the union of the keys in the
// passed in object, and the type is FC with the prop types set by the second type parameter.
type IconMap<IconsObject, IconProps> = {
  [IconName in keyof IconsObject]: FC<IconProps>;
};

// A wrapper for the Object.entries function that outputs a typed array
// instead of [key: string, value: any]
const typedEntries = <IconName extends string | number | symbol, Component>(
  myObj: Record<IconName, Component>
): [IconName, Component][] => Object.entries(myObj) as [IconName, Component][];

// Reads the icons object
// Converts it to an array of key-value pairs, adding the types
// Loops over the array and creates a new object where the value (icon) is wrapped in the IconWrapper
const wrapIcons = <IconsObject,>(icons: IconMap<IconsObject, IIconColor>) =>
  typedEntries(icons).reduce<IconMap<IconsObject, IIcon>>(
    (obj, [iconName, Icon]) => {
      obj[iconName] = (props) => (
        <IconWrapper {...props}>
          <Icon {...props.color} />
        </IconWrapper>
      );
      return obj;
    },
    {} as IconMap<IconsObject, IIcon>
  );

type IColor = {
  defaultColor: string;
  color?: string;
};

function getColor({ defaultColor, color }: IColor) {
  const usedColor = color || defaultColor;
  if (usedColor.startsWith('--')) return `var(${usedColor})`;
  return usedColor;
}

/**
 * @deprecated Avoid using ui-components. Try to split out functionality into
 * smaller libraries instead.
 */
export const Icons = wrapIcons({
  Caret: ({ primary }: IIconColor) => (
    <svg
      fill={getColor({ defaultColor: 'none', color: primary })}
      role="img"
      aria-label="caret icon"
      viewBox="0 0 20 20"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M15.833 7.5 10 13.333 4.167 7.5"
      />
    </svg>
  ),
  CircleInfo: ({ primary }: IIconColor) => (
    <svg viewBox="0 0 18 17">
      <title>Circle info</title>
      <path
        fill={getColor({ defaultColor: '#212529', color: primary })}
        fillRule="evenodd"
        d="M17.447 8.66a8.25 8.25 0 1 1-16.5 0 8.25 8.25 0 0 1 16.5 0ZM9.939 5.61a.698.698 0 1 1-1.397 0 .698.698 0 0 1 1.397 0Zm-.037 6.114V7.72a.602.602 0 1 0-1.204 0v4.005a.602.602 0 0 0 1.204 0Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  CircleInfoBlack: ({ primary }: IIconColor) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 17">
      <path
        fill={getColor({ defaultColor: '#212529', color: primary })}
        fillRule="evenodd"
        d="M17.447 8.66a8.25 8.25 0 1 1-16.5 0 8.25 8.25 0 0 1 16.5 0ZM9.939 5.61a.698.698 0 1 1-1.397 0 .698.698 0 0 1 1.397 0Zm-.037 6.114V7.72a.602.602 0 1 0-1.204 0v4.005a.602.602 0 0 0 1.204 0Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  CircleInfoOutline: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11 14H10V10H9M10 6H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
        stroke={getColor({ defaultColor: '--info-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  CirclePromptBlack: ({ primary }: IIconColor) => (
    <svg viewBox="0 0 17 17">
      <path
        fill={getColor({ defaultColor: '#212529', color: primary })}
        fillRule="evenodd"
        d="M8.447 16.66a8.25 8.25 0 1 0 0-16.5 8.25 8.25 0 0 0 0 16.5Zm.337-6.74H7.767v-.504c0-.721.366-1.213 1.162-1.66.675-.389.936-.733.936-1.312v-.006c0-.683-.564-1.175-1.424-1.175-.878 0-1.419.487-1.494 1.213l-.006.055h-.994l.006-.07c.093-1.116.895-2.05 2.529-2.05 1.459 0 2.465.847 2.465 1.978v.005c0 .853-.471 1.46-1.215 1.88-.739.41-.948.716-.948 1.29v.355Zm.157 1.868c0 .35-.285.623-.663.623-.372 0-.663-.273-.663-.623 0-.355.291-.623.663-.623.378 0 .663.268.663.623Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  CircleWarningWhite: ({ primary }: IIconColor) => (
    <svg width="21" height="21" viewBox="0 0 21 21">
      <title>Invalid</title>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.86816 10.6381C2.86816 6.08178 6.56181 2.38812 11.1182 2.38812C15.6745 2.38812 19.3682 6.08177 19.3682 10.6381C19.3682 15.1945 15.6745 18.8881 11.1182 18.8881C6.56182 18.8881 2.86816 15.1945 2.86816 10.6381ZM10.3763 13.6898C10.3763 13.3041 10.689 12.9914 11.0747 12.9914C11.4604 12.9914 11.7731 13.3041 11.7731 13.6898C11.7731 14.0755 11.4604 14.3881 11.0747 14.3881C10.689 14.3881 10.3763 14.0755 10.3763 13.6898ZM10.4134 7.57485L10.4134 11.5796C10.4134 11.9119 10.6827 12.1812 11.015 12.1812C11.3472 12.1812 11.6166 11.9119 11.6166 11.5796L11.6166 7.57485C11.6166 7.24259 11.3472 6.97325 11.015 6.97325C10.6827 6.97325 10.4134 7.24259 10.4134 7.57485Z"
        fill={getColor({ defaultColor: 'white', color: primary })}
      />
    </svg>
  ),
  CircleCheckMarkValid: ({ primary, secondary }: IIconColor) => (
    <svg
      width="30"
      height="31"
      viewBox="0 0 30 31"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Valid</title>
      <g clipPath="url(#clip0_994_35535)">
        <circle
          cx="14.9993"
          cy="15.3802"
          r="14.9993"
          fill={getColor({ defaultColor: '#00B695', color: primary })}
        />
        <path
          d="M23.3261 9.12891L12.771 21.6284L6.66016 16.5149"
          stroke={getColor({ defaultColor: '#E5F8F4', color: secondary })}
          strokeWidth="2.99987"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_994_35535">
          <rect
            width="29.9987"
            height="29.9987"
            fill="white"
            transform="translate(0 0.380859)"
          />
        </clipPath>
      </defs>
    </svg>
  ),
  CheckMarkGreen: ({ primary }: IIconColor) => (
    <svg width="19" height="15" viewBox="0 0 19 15" fill="none">
      <path
        d="M17.11 1L6.907 13.0825L1 8.13966"
        stroke={getColor({ defaultColor: '#4B9B59', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  CheckMarkWhite: ({ primary }: IIconColor) => (
    <svg width="19" height="15" viewBox="0 0 19 15" fill="none">
      <path
        d="M17.11 1L6.907 13.0825L1 8.13966"
        stroke={getColor({ defaultColor: '--light-100', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  CloseButton: ({ primary, secondary }: IIconColor) => (
    <svg width="20" height="20" viewBox="0 0 20 20">
      <title>Close</title>
      <g clipPath="url(#a)">
        <path
          fill={getColor({ defaultColor: '#767676', color: primary })}
          d="M10 20a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z"
        />
        <path
          stroke={getColor({ defaultColor: '#fff', color: secondary })}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          d="m14 6-8 8m0-8 8 8"
        />
      </g>
      <defs>
        <clipPath id="a">
          <path fill="#fff" d="M0 0h20v20H0z" />
        </clipPath>
      </defs>
    </svg>
  ),
  Clock: ({ primary, secondary }: IIconColor) => (
    <svg fill="none" viewBox="0 0 32 32">
      <path
        fill={getColor({ defaultColor: '--dark-100', color: primary })}
        stroke={getColor({ defaultColor: '--light-100', color: secondary })}
        fillRule="evenodd"
        d="M29.333 16c0 7.364-5.97 13.333-13.333 13.333-7.364 0-13.333-5.97-13.333-13.333C2.667 8.636 8.637 2.667 16 2.667c7.364 0 13.333 5.97 13.333 13.333ZM17 8a1 1 0 0 0-2 0v8a1 1 0 0 0 .553.894l5.333 2.667a1 1 0 1 0 .895-1.789L17 15.382V8Z"
        clipRule="evenodd"
      />
    </svg>
  ),

  ArrowRight: ({ primary }: IIconColor) => (
    <svg
      width="17"
      height="15"
      viewBox="0 0 17 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.5 7.86182C0.5 7.30953 0.947715 6.86182 1.5 6.86182L13.0858 6.86182L8.79289 2.56892C8.40237 2.1784 8.40237 1.54523 8.79289 1.15471C9.18342 0.764185 9.81658 0.764185 10.2071 1.15471L16.2071 7.15471C16.5976 7.54523 16.5976 8.1784 16.2071 8.56892L10.2071 14.5689C9.81658 14.9594 9.18342 14.9594 8.79289 14.5689C8.40237 14.1784 8.40237 13.5452 8.79289 13.1547L13.0858 8.86182L1.5 8.86182C0.947715 8.86182 0.5 8.4141 0.5 7.86182Z"
        fill={getColor({ defaultColor: '#212529', color: primary })}
      />
    </svg>
  ),
  TrustPilotStar: () => (
    <svg role="img" aria-label="trustpilot star" viewBox="0 0 46 46">
      <path d="M0 46.330002h46.375586V0H0z" fill="#00b67a"></path>
      <path
        fill="white"
        d="M39.533936 19.711433L13.230239 38.80065l3.838216-11.797827L7.02115 19.711433h12.418975l3.837417-11.798624 3.837418 11.798624h12.418975zM23.2785 31.510075l7.183595-1.509576 2.862114 8.800152L23.2785 31.510075z"
      ></path>
    </svg>
  ),
  AxoFinans: () => (
    <svg width="20.2" height="18.5" viewBox="0 0 113 103">
      <path
        d="M56.5 102.8c-29 0-48.9-7-50.5-7.5-2.1-.7-5.6-2.5-5.6-6.8V14.9c0-2.2 1-5.2 5.6-6.8C7.7 7.4 27 .3 56.5.3s48.8 7.1 50.6 7.8c4.6 1.6 5.6 4.5 5.6 6.8v73.6c0 3.2-1.9 5.5-5.5 6.8-1.7.6-20 7.5-50.7 7.5z"
        fill="#fff"
      />
      <path
        d="M109.5 89.3c0 1.5-1.1 2.2-2.8 2.8 0 0-18.4 7.5-50.2 7.5-29.9 0-50.2-7.5-50.2-7.5-1.6-.5-2.8-1.3-2.8-2.8V14.1c0-1.5 1.2-2.3 2.8-2.8 0 0 19.7-7.7 50.2-7.7 30.5 0 50.2 7.7 50.2 7.7 1.6.5 2.8 1.3 2.8 2.8v75.2z"
        fill="#ffc20e"
      />
      <path
        d="M17.5 71.2h9v2.6h-5.4v2.7h5.3v2.6h-5.3V84h-3.5V71.2zm12.7 0h3.5v12.9h-3.5V71.2zm7.7 0h4.4l3.2 6.3 1.1 2.8h.2l-.2-2.7v-6.3h3.3v12.9h-4.5l-3.3-6.6c-.5-1-.9-2-.9-2.5h-.1l.1 2v7.1h-3.3v-13zm28.6 12.9h-3.7l-.8-2.7h-4.8l-.9 2.6h-3.5l4.7-12.9H62l4.5 13zm-5.3-5.2-1-3-.5-2h-.1c-.1.7-.4 1.5-.6 2l-1 3h3.2zm8.3-7.7h4.4l3.2 6.3 1.1 2.8h.2l-.2-2.7v-6.3h3.3v12.9H77l-3.3-6.6c-.5-1-.9-2-.9-2.5h-.1l.1 2v7.1h-3.3v-13zm18.6 8.7a2 2 0 0 0 2.1 1.8c1 0 1.8-.4 1.8-1.2 0-.7-.4-1-1.7-1.4l-2.1-.7c-1.6-.6-3-1.7-3-3.5 0-2.7 2.4-4 5.4-4 3.7 0 4.3 2.2 4.6 2.9l-3.1 1c-.1-.4-.5-1.4-1.7-1.4s-1.7.5-1.7 1.1c0 .7.4.9 1.7 1.4l2.4.9c1.7.6 2.7 1.6 2.7 3.3 0 1.9-1.3 4.2-5.5 4.2-3.9 0-4.7-2.4-5-3.2l3.1-1.2z"
        fill="#fff"
      />
      <path
        d="M16.7 34.7c1.6-4.8 5.3-9 12.4-9 9.3 0 11.6 6.4 11.6 12.5v15.7l.2 8.9h-3.6l-.2-5.8c-2.9 3.2-6.3 6.6-11.9 6.6-6.4 0-10.2-4.4-10.2-10.2C15 42.3 25.2 41 34.6 41h2.5c-.2-6.5-.4-12-8.4-12-5.6 0-7.4 4-8.7 6.9l-3.3-1.2zm20.4 9.2h-2.4c-9.1 0-15.8 1.2-15.8 9 0 5.4 3.7 7.1 6.9 7.1 4.5 0 6.9-2.5 11.3-6.7v-9.4zm6.3-17.5h4.4l9 14.5 9.7-14.5h4.1L59.2 43.5l12.3 19.3H67L56.8 46.4 46.2 62.8h-4.4l12.6-19.1-11-17.3z"
        fill="#111"
      />
      <path
        d="M83.8 25.7c12.1 0 14.7 11.7 14.7 19.1 0 9.2-4.2 18.8-14.7 18.8S69.1 54 69.1 44.8c0-7.4 2.6-19.1 14.7-19.1zm0 34.6c6.7 0 10.9-5.5 10.9-16.3 0-7.4-3-15-10.9-15s-10.9 7.6-10.9 15c0 10.8 4.2 16.3 10.9 16.3zM96.3 28c0-1.5 1.2-2.3 2.4-2.3s2.4.8 2.4 2.3c0 1.5-1.2 2.3-2.4 2.3-1.3 0-2.4-.9-2.4-2.3zm4 0c0-1-.7-1.7-1.6-1.7-.9 0-1.7.7-1.7 1.7s.7 1.7 1.6 1.7c.9 0 1.7-.7 1.7-1.7zm-2 1.2h-.6v-2.5h.9c.7 0 1.1.2 1.1.8 0 .5-.3.6-.7.7l.6 1.1H99l-.6-1h-.2v.9zm.5-1.5c.2 0 .3-.1.3-.3 0-.2-.3-.3-.5-.3h-.3v.6h.5z"
        fill="#111"
      />
    </svg>
  ),
  BankID: ({ primary }) => (
    <svg
      width="18"
      height="13"
      viewBox="0 0 18 13"
      fill={getColor({ defaultColor: '#212529', color: primary })}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.3195 10.4205H13.028C12.8955 10.4205 12.7874 10.5254 12.7874 10.6549V11.7773C12.7874 11.9064 12.8955 12.0114 13.028 12.0114H17.3195C17.452 12.0114 17.5601 11.9064 17.5601 11.7773V10.6549C17.5601 10.5254 17.452 10.4205 17.3195 10.4205ZM4.59216 10.4205H0.300619C0.168156 10.4205 0.0600586 10.5254 0.0600586 10.6549V11.7773C0.0600586 11.9064 0.168156 12.0114 0.300619 12.0114H4.59216C4.72463 12.0114 4.83273 11.9064 4.83273 11.7773V10.6549C4.83273 10.5254 4.72463 10.4205 4.59216 10.4205ZM10.9559 7.23864H6.6644C6.53193 7.23864 6.42384 7.34357 6.42384 7.47313V8.59549C6.42384 8.72461 6.53193 8.82955 6.6644 8.82955H10.9559C11.0884 8.82955 11.1965 8.72461 11.1965 8.59549V7.47313C11.1965 7.34357 11.0884 7.23864 10.9559 7.23864ZM4.59216 7.23864H0.300619C0.168156 7.23864 0.0600586 7.34357 0.0600586 7.47313V8.59549C0.0600586 8.72461 0.168156 8.82955 0.300619 8.82955H4.59216C4.72463 8.82955 4.83273 8.72461 4.83273 8.59549V7.47313C4.83273 7.34357 4.72463 7.23864 4.59216 7.23864ZM17.3195 4.0555H13.0275C12.895 4.0555 12.7874 4.16044 12.7874 4.28999V5.41235C12.7874 5.54147 12.895 5.64641 13.028 5.64641H17.3195C17.452 5.64641 17.5601 5.54147 17.5601 5.41235V4.28999C17.5601 4.16044 17.452 4.0555 17.3195 4.0555ZM10.9559 4.05682H6.6644C6.53193 4.05682 6.42384 4.16176 6.42384 4.29131V5.41367C6.42384 5.54279 6.53193 5.64773 6.6644 5.64773H10.9559C11.0884 5.64773 11.1965 5.54279 11.1965 5.41367V4.29131C11.1965 4.16176 11.0884 4.05682 10.9559 4.05682ZM17.3195 0.875H13.028C12.8955 0.875 12.7874 0.979938 12.7874 1.10949V2.23185C12.7874 2.36097 12.8955 2.46591 13.028 2.46591H17.3195C17.452 2.46591 17.5601 2.36097 17.5601 2.23185V1.10949C17.5601 0.979938 17.452 0.875 17.3195 0.875ZM4.59216 0.875H0.300619C0.168156 0.875 0.0600586 0.979938 0.0600586 1.10949V2.23185C0.0600586 2.36097 0.168156 2.46591 0.300619 2.46591H4.59216C4.72463 2.46591 4.83273 2.36097 4.83273 2.23185V1.10949C4.83273 0.979938 4.72463 0.875 4.59216 0.875Z"
      />
    </svg>
  ),
  Crown: ({ primary }) => (
    <svg
      width="26"
      height="26"
      viewBox="0 0 26 26"
      fill={getColor({ defaultColor: 'white', color: primary })}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M4.31153 20.2147H5.56267C5.56267 19.5383 5.8407 18.3358 5.49316 17.6594C5.07612 16.9079 4.17252 16.1563 3.33842 15.2545C2.99088 14.8787 2.2263 14.2775 2.2263 13.3004C2.2263 11.9476 3.1299 11.5719 3.82498 11.5719C4.0335 11.5719 4.24202 11.5719 4.52006 11.647C4.58956 11.1961 4.79809 10.7452 5.07612 10.3694C4.72858 10.2191 4.31153 10.1439 3.89448 10.1439C2.71285 10.1439 0.975159 11.0458 0.975159 13.3004C0.975159 14.8035 1.80925 15.6303 2.64335 16.457C3.47744 17.434 4.24202 18.3358 4.31153 20.2147Z" />
      <path d="M21.619 21.2672H4.31152V22.62H21.619V21.2672Z" />
      <path d="M21.619 23.7472H4.31152V25.1H21.619V23.7472Z" />
      <path d="M7.99548 20.2149H9.38564C9.52465 19.0875 9.9417 18.1105 9.66367 17.2838C9.38564 16.3819 8.27351 15.3298 7.57844 14.5782C7.02237 13.9018 6.67483 13.3757 6.67483 12.549C6.67483 11.6472 7.36991 10.8204 8.34302 10.8204C9.45515 10.8204 10.1502 11.9478 11.5404 11.9478V10.595C10.7063 10.5198 9.9417 9.46765 8.34302 9.46765C7.50893 9.46765 6.74434 9.76827 6.18828 10.3695C5.70173 10.9708 5.35419 11.7223 5.35419 12.549C5.35419 13.8267 5.91025 14.6534 6.46631 15.4049C7.2309 16.4571 7.99548 17.5844 7.99548 20.2149Z" />
      <path d="M22.1055 11.5719C22.731 11.5719 23.7041 11.9476 23.7041 13.3004C23.7041 14.3526 22.9396 14.8787 22.592 15.2545C21.7579 16.0812 20.8543 16.8327 20.4373 17.6594C20.0897 18.3358 20.2983 19.5383 20.3678 20.2147H21.6189C21.7579 18.3358 22.453 17.434 23.3566 16.457C24.1212 15.6303 25.0248 14.7284 25.0248 13.3004C25.0248 11.0458 23.3566 10.1439 22.1055 10.1439C21.6884 10.1439 21.3409 10.2191 20.9238 10.3694C21.2019 10.7452 21.3409 11.1961 21.4799 11.647C21.6884 11.5719 21.8969 11.5719 22.1055 11.5719Z" />
      <path d="M14.5292 11.9478C15.9194 11.9478 16.545 10.8204 17.7266 10.8204C18.6997 10.8204 19.3948 11.6472 19.3948 12.549C19.3948 13.3757 19.0472 13.9018 18.4912 14.5782C17.7961 15.4049 16.684 16.3819 16.4059 17.2838C16.1279 18.1105 16.545 19.0875 16.684 20.2149H18.0741C18.0046 17.5844 18.8387 16.3819 19.5338 15.4049C20.0898 14.6534 20.6459 13.8267 20.6459 12.549C20.6459 11.7223 20.3679 10.8956 19.8118 10.3695C19.2558 9.76827 18.4912 9.46765 17.6571 9.46765C16.0584 9.46765 15.2938 10.5198 14.4597 10.595V11.9478H14.5292Z" />
      <path d="M12.305 9.54285V13.5261C12.305 16.0814 11.6794 16.6826 11.6794 17.5845C11.6794 18.6366 12.0964 19.0124 12.305 20.2149H13.6256C13.8341 19.0124 14.2512 18.7118 14.2512 17.5845C14.2512 16.7578 13.6256 16.0814 13.6256 13.5261V9.54285H12.305Z" />
      <path d="M13 6.08574C13.3475 6.08574 13.6951 6.38636 13.6951 6.83729C13.6951 7.21307 13.417 7.58884 13 7.58884C12.6525 7.58884 12.3049 7.28822 12.3049 6.83729C12.3049 6.46152 12.5829 6.08574 13 6.08574ZM12.3744 5.03357C11.6793 5.25903 11.1928 6.01058 11.1928 6.83729C11.1928 7.88947 12.0269 8.79133 13 8.79133C13.9731 8.79133 14.8072 7.88947 14.8072 6.83729C14.8072 6.01058 14.3206 5.25903 13.6256 5.03357V3.60561H14.8767V2.32797H13.5561V0.900024H12.3744V2.32797H11.0538V3.60561H12.3744V5.03357Z" />
    </svg>
  ),
  AvtaleGiro: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      viewBox="0 0 339.2 64.9"
      xmlSpace="preserve"
    >
      <style type="text/css">{'\n\t.st0{fill:#000000;}\n'}</style>
      <title>Avtale Giro logo</title>
      <g id="Layer_2">
        <g id="Layer_1-2">
          <path
            className="st0"
            d="M303.9,29.5c-2.8-0.1-5.2,2-5.4,4.8c0,0.2,0,0.3,0,0.5c0,3,2.4,5.4,5.4,5.4s5.4-2.4,5.4-5.4    c0.1-2.8-2.1-5.2-4.9-5.3C304.2,29.5,304.1,29.5,303.9,29.5z"
          />
          <path
            className="st0"
            d="M337.2,0H0v64.9h339.2V0H337.2z M253.7,12.8c1.9,0,3.5,1.6,3.5,3.5s-1.6,3.5-3.5,3.5c-1.9,0-3.5-1.6-3.5-3.5    C250.2,14.4,251.7,12.8,253.7,12.8C253.6,12.8,253.7,12.8,253.7,12.8L253.7,12.8z M197.5,61H4V4h193.5V61z M242.3,47    c-4.4,1-8.9,1.5-13.4,1.6c-14.6,0-19.8-9.7-19.8-17.1c0-10.6,8.4-17.9,20.6-17.9c4.1,0,8.1,0.8,11.9,2.3v8.5    c-3.1-1.8-6.6-2.8-10.1-2.9c-6.2,0-10.4,3.9-10.4,9.7c0,7.2,4.7,10.4,12.1,9.3V36h-4.2v-7.2h13.4L242.3,47z M258.9,48h-10.4V21.7    h10.4L258.9,48z M281.1,31.5c-2.5,0-4.3,2.5-4.9,5.5V48h-10.4V21.7h9.4v7h0.1c1.9-4.9,4.8-7.5,8.4-7.5c1.6,0,3.2,0.4,4.7,1.1    L284.6,33C283.6,32.1,282.4,31.6,281.1,31.5L281.1,31.5z M304,48.5c-11.2,0-15.6-7.2-15.6-13.6c0-6.8,4.6-13.6,15.5-13.6    c11.2,0,15.5,7.2,15.5,13.6C319.4,41.8,314.5,48.5,304,48.5z"
          />
          <path
            className="st0"
            d="M32.9,36.4l5.2-13.5l5.2,13.5H32.9z M16.4,48h11.8l2.2-5.5h15.4L48,48h11.9L44.3,14H31.9L16.4,48z"
          />
          <path
            className="st0"
            d="M55.2,21.7h11.1l5.5,14l5.5-14h8.3L74.3,48h-7.4L55.2,21.7z"
          />
          <path
            className="st0"
            d="M87.3,23.9l11.3-10.1h2.4v7.9h8.3v6.6H101v9.9c0,2.2,1.3,3.2,3.5,3.2c1.9,0,3.8-0.7,5.4-1.8v5.9    c-2.6,2-5.8,3-9.1,2.9c-3.1,0-6.3-0.9-8.2-2.8c-1.5-1.5-2-3.4-2-7V28.3h-3.4L87.3,23.9z"
          />
          <path
            className="st0"
            d="M128.1,40.3c0,0.8,0,1.6,0.2,2.4c-1.4,1.1-3.1,1.7-4.9,1.8c-1.6,0-2.9-1.2-2.9-2.8c0-0.1,0-0.1,0-0.2    c0-3.6,5.1-3.3,7.6-5V40.3z M140.8,41.3c-2.9,0-3.1-0.8-3.1-3.5v-7.5c0-6.6-5.7-9.1-11.8-9.1c-4.5-0.1-9,1.5-12.4,4.5v7.7    c4.4-3.3,7.1-4.5,10.5-4.5c2.9,0,4,0.9,4.1,3.2c-2.8,2.1-10.7,2.5-13.5,5.3c-1.2,1.2-2,2.8-2,4.5c0,3.4,2.5,6.4,8.4,6.4    c3.1,0.1,6.1-0.8,8.6-2.5c1.6,2.2,4.2,2.8,9.3,2.4L140.8,41.3z"
          />
          <path className="st0" d="M144.6,14H155v34h-10.4V14z" />
          <path
            className="st0"
            d="M168.5,32.9c-0.2-4.1,1.9-5.9,4.5-5.9c3,0,4.5,2,4.5,5.9L168.5,32.9z M184.6,39c-2.4,1.9-5.4,2.9-8.4,3    c-4,0-7.2-2.5-7.2-5.2h17.2c1.1-9.1-5.1-15.6-13.2-15.6c-7.3-0.2-13.4,5.6-13.6,12.9c0,0.2,0,0.4,0,0.6c0,7.7,5.9,13.7,15,13.7    c5,0,9.3-1.8,12.2-5.1L184.6,39z"
          />
        </g>
      </g>
    </svg>
  ),
  ThumbUp: ({ primary }: IIconColor) => (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.09998 8.89998C2.09998 8.23723 2.63723 7.69998 3.29998 7.69998C3.96272 7.69998 4.49998 8.23723 4.49998 8.89998V13.7C4.49998 14.3627 3.96272 14.9 3.29998 14.9C2.63723 14.9 2.09998 14.3627 2.09998 13.7V8.89998Z"
        fill={getColor({ defaultColor: '#fff', color: primary })}
      />
      <path
        d="M5.29998 8.76664V13.1111C5.29998 13.7172 5.64238 14.2712 6.18443 14.5422L6.22431 14.5621C6.66865 14.7843 7.15861 14.9 7.65539 14.9H11.9883C12.751 14.9 13.4076 14.3616 13.5572 13.6138L14.5172 8.81376C14.7152 7.8237 13.958 6.89998 12.9483 6.89998H10.1V3.69998C10.1 2.81632 9.38363 2.09998 8.49998 2.09998C8.05815 2.09998 7.69998 2.45815 7.69998 2.89998V3.43331C7.69998 4.12569 7.47541 4.7994 7.05998 5.35331L5.93998 6.84664C5.52454 7.40055 5.29998 8.07426 5.29998 8.76664Z"
        fill={getColor({ defaultColor: '#fff', color: primary })}
      />
    </svg>
  ),
  LightBulb: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11 3C11 2.44772 10.5523 2 10 2C9.44771 2 9 2.44772 9 3V4C9 4.55228 9.44771 5 10 5C10.5523 5 11 4.55228 11 4V3Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M15.6568 5.75731C16.0473 5.36678 16.0473 4.73362 15.6568 4.34309C15.2663 3.95257 14.6331 3.95257 14.2426 4.34309L13.5355 5.0502C13.145 5.44072 13.145 6.07389 13.5355 6.46441C13.926 6.85494 14.5592 6.85494 14.9497 6.46441L15.6568 5.75731Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M18 10C18 10.5523 17.5523 11 17 11H16C15.4477 11 15 10.5523 15 10C15 9.44771 15.4477 9 16 9H17C17.5523 9 18 9.44771 18 10Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M5.05019 6.46443C5.44071 6.85496 6.07388 6.85496 6.4644 6.46443C6.85493 6.07391 6.85493 5.44074 6.4644 5.05022L5.7573 4.34311C5.36677 3.95259 4.73361 3.95259 4.34308 4.34311C3.95256 4.73363 3.95256 5.3668 4.34308 5.75732L5.05019 6.46443Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M5 10C5 10.5523 4.55228 11 4 11H3C2.44772 11 2 10.5523 2 10C2 9.44771 2.44772 9 3 9H4C4.55228 9 5 9.44771 5 10Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M8 16V15H12V16C12 17.1046 11.1046 18 10 18C8.89543 18 8 17.1046 8 16Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
      <path
        d="M12.0009 14C12.0155 13.6597 12.2076 13.3537 12.4768 13.1411C13.4046 12.4086 14 11.2738 14 10C14 7.79086 12.2091 6 10 6C7.79086 6 6 7.79086 6 10C6 11.2738 6.59545 12.4086 7.52319 13.1411C7.79241 13.3537 7.98451 13.6597 7.99911 14H12.0009Z"
        fill={getColor({ defaultColor: '#111827', color: primary })}
      />
    </svg>
  ),
  Shield: ({ primary }: IIconColor) => (
    <svg
      width="52"
      height="54"
      viewBox="0 0 52 54"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.93169 10.6804C10.5581 10.5498 19.329 6.87698 26.0001 0.906189C32.6712 6.87698 41.4421 10.5498 51.0686 10.6804C51.4181 12.7629 51.6001 14.9022 51.6001 17.084C51.6001 33.8036 40.9146 48.0274 26.0001 53.2989C11.0857 48.0274 0.400146 33.8036 0.400146 17.084C0.400146 14.9022 0.58211 12.7629 0.93169 10.6804ZM36.5281 21.212C37.0407 20.6994 37.0407 19.8684 36.5281 19.3558C36.0156 18.8433 35.1846 18.8433 34.672 19.3558L22.8001 31.2278L17.3282 25.7558C16.8156 25.2433 15.9846 25.2433 15.472 25.7558C14.9594 26.2684 14.9594 27.0994 15.472 27.612L21.872 34.012C22.1181 34.2581 22.452 34.3964 22.8001 34.3964C23.1482 34.3964 23.482 34.2581 23.7282 34.012L36.5281 21.212Z"
        fill={getColor({ defaultColor: '--info-900', color: primary })}
      />
    </svg>
  ),
  Lifesaver: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 10.684C18 15.1022 14.4183 18.684 10 18.684V20.684C15.5228 20.684 20 16.2068 20 10.684H18ZM10 18.684C5.58172 18.684 2 15.1022 2 10.684H0C0 16.2068 4.47715 20.684 10 20.684V18.684ZM2 10.684C2 6.26568 5.58172 2.68396 10 2.68396V0.68396C4.47715 0.68396 0 5.16111 0 10.684H2ZM10 2.68396C14.4183 2.68396 18 6.26568 18 10.684H20C20 5.16111 15.5228 0.68396 10 0.68396V2.68396ZM13 10.684C13 12.3408 11.6569 13.684 10 13.684V15.684C12.7614 15.684 15 13.4454 15 10.684H13ZM10 13.684C8.34315 13.684 7 12.3408 7 10.684H5C5 13.4454 7.23858 15.684 10 15.684V13.684ZM7 10.684C7 9.02711 8.34315 7.68396 10 7.68396V5.68396C7.23858 5.68396 5 7.92254 5 10.684H7ZM10 7.68396C11.6569 7.68396 13 9.02711 13 10.684H15C15 7.92254 12.7614 5.68396 10 5.68396V7.68396ZM15.6569 3.61289L12.1213 7.14843L13.5355 8.56264L17.0711 5.02711L15.6569 3.61289ZM12.1213 14.2195L15.6569 17.755L17.0711 16.3408L13.5355 12.8053L12.1213 14.2195ZM7.87868 7.14843L4.34315 3.61289L2.92893 5.02711L6.46447 8.56264L7.87868 7.14843ZM6.46447 12.8053L2.92893 16.3408L4.34315 17.755L7.87868 14.2195L6.46447 12.8053Z"
        fill={getColor({ defaultColor: '--info-900', color: primary })}
      />
    </svg>
  ),
  NoCost: ({ primary }: IIconColor) => (
    <svg
      width="16"
      height="21"
      viewBox="0 0 16 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5 12.684L11 6.68396M5.50003 7.18396H5.51003M10.5 12.184H10.51M15 19.684V3.68396C15 2.57939 14.1046 1.68396 13 1.68396H3C1.89543 1.68396 1 2.57939 1 3.68396V19.684L4.5 17.684L8 19.684L11.5 17.684L15 19.684ZM6 7.18396C6 7.4601 5.77614 7.68396 5.5 7.68396C5.22386 7.68396 5 7.4601 5 7.18396C5 6.90782 5.22386 6.68396 5.5 6.68396C5.77614 6.68396 6 6.90782 6 7.18396ZM11 12.184C11 12.4601 10.7761 12.684 10.5 12.684C10.2239 12.684 10 12.4601 10 12.184C10 11.9078 10.2239 11.684 10.5 11.684C10.7761 11.684 11 11.9078 11 12.184Z"
        stroke={getColor({ defaultColor: '--info-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Money: ({ primary, secondary }: IIconColor) => (
    <svg
      width="20"
      height="17"
      viewBox="0 0 20 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15 5.68396V3.68396C15 2.57939 14.1046 1.68396 13 1.68396H3C1.89543 1.68396 1 2.57939 1 3.68396V9.68396C1 10.7885 1.89543 11.684 3 11.684H5M7 15.684H17C18.1046 15.684 19 14.7885 19 13.684V7.68396C19 6.57939 18.1046 5.68396 17 5.68396H7C5.89543 5.68396 5 6.57939 5 7.68396V13.684C5 14.7885 5.89543 15.684 7 15.684ZM14 10.684C14 11.7885 13.1046 12.684 12 12.684C10.8954 12.684 10 11.7885 10 10.684C10 9.57939 10.8954 8.68396 12 8.68396C13.1046 8.68396 14 9.57939 14 10.684Z"
        stroke={getColor({ defaultColor: '--info-900', color: primary })}
        fill={getColor({ defaultColor: 'none', color: secondary })}
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Globe: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.05493 9.68396H3C4.10457 9.68396 5 10.5794 5 11.684V12.684C5 13.7885 5.89543 14.684 7 14.684C8.10457 14.684 9 15.5794 9 16.684V19.629M6 2.61948V4.18396C6 5.56467 7.11929 6.68396 8.5 6.68396H9C10.1046 6.68396 11 7.57939 11 8.68396C11 9.78853 11.8954 10.684 13 10.684C14.1046 10.684 15 9.78853 15 8.68396C15 7.57939 15.8954 6.68396 17 6.68396L18.0645 6.68396M13 19.1718V16.684C13 15.5794 13.8954 14.684 15 14.684H18.0645M19 10.684C19 15.6545 14.9706 19.684 10 19.684C5.02944 19.684 1 15.6545 1 10.684C1 5.7134 5.02944 1.68396 10 1.68396C14.9706 1.68396 19 5.7134 19 10.684Z"
        stroke={getColor({ defaultColor: '--info-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Reload: ({ primary }: IIconColor) => (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.65 3.03396C12.2 1.58396 10.21 0.68396 8.00001 0.68396C3.58001 0.68396 0.0100098 4.26396 0.0100098 8.68396C0.0100098 13.104 3.58001 16.684 8.00001 16.684C11.73 16.684 14.84 14.134 15.73 10.684H13.65C12.83 13.014 10.61 14.684 8.00001 14.684C4.69001 14.684 2.00001 11.994 2.00001 8.68396C2.00001 5.37396 4.69001 2.68396 8.00001 2.68396C9.66001 2.68396 11.14 3.37396 12.22 4.46396L9.00001 7.68396H16V0.68396L13.65 3.03396Z"
        fill={getColor({ defaultColor: '--info-900', color: primary })}
      />
    </svg>
  ),
  ShieldOutline: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7 11L9 13L13 9M18.6179 4.98433C18.4132 4.99471 18.2072 4.99996 18 4.99996C14.9265 4.99996 12.123 3.84452 9.99993 1.94432C7.87691 3.84445 5.07339 4.99984 2 4.99984C1.79277 4.99984 1.58678 4.99459 1.38213 4.98421C1.1327 5.94782 1 6.95841 1 8C1 13.5915 4.82432 18.2898 10 19.6219C15.1757 18.2898 19 13.5915 19 8C19 6.95846 18.8673 5.9479 18.6179 4.98433Z"
        stroke={getColor({ defaultColor: '--info-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Pdf: ({ primary }: IIconColor) => (
    <svg
      width="16"
      height="21"
      viewBox="0 0 16 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 20.3679H2C0.89543 20.3679 0 19.4725 0 18.3679V2.36791C0 1.26334 0.89543 0.367906 2 0.367906H9C9.00896 0.366744 9.01804 0.366744 9.027 0.367906H9.033C9.04244 0.370864 9.05216 0.372874 9.062 0.373906C9.15019 0.37956 9.23726 0.396705 9.321 0.424906H9.336H9.351H9.363C9.38145 0.437832 9.39884 0.452211 9.415 0.467906C9.52394 0.51633 9.62321 0.584091 9.708 0.667906L15.708 6.66791C15.7918 6.75269 15.8596 6.85196 15.908 6.96091C15.917 6.98291 15.924 7.00391 15.931 7.02691L15.941 7.05491C15.9689 7.1383 15.9854 7.22509 15.99 7.31291C15.9909 7.32287 15.9932 7.33264 15.997 7.34191V7.34791C15.9986 7.35446 15.9996 7.36116 16 7.36791V18.3679C16 18.8983 15.7893 19.407 15.4142 19.7821C15.0391 20.1572 14.5304 20.3679 14 20.3679ZM10.424 12.3679V17.3679H11.364V15.3279H12.824V14.4899H11.364V13.2089H13V12.3679H10.424ZM6.724 12.3679V17.3679H7.93C8.43593 17.3928 8.92583 17.1868 9.262 16.8079C9.6069 16.3678 9.77967 15.8171 9.748 15.2589V14.4489C9.77117 13.8965 9.59356 13.3545 9.248 12.9229C8.92263 12.5525 8.44761 12.3486 7.955 12.3679H6.724ZM3 12.3679V17.3679H3.94V15.6089H4.566C4.98402 15.632 5.39232 15.4773 5.69 15.1829C5.97408 14.8653 6.12141 14.4485 6.1 14.0229C6.11856 13.5871 5.97135 13.1605 5.688 12.8289C5.40772 12.5181 5.00309 12.349 4.585 12.3679H3ZM9 2.36791V7.36791H14L9 2.36791ZM7.946 16.5299H7.664V13.2089H8.006C8.24885 13.1946 8.48236 13.3046 8.626 13.5009C8.77261 13.8042 8.83544 14.1412 8.808 14.4769V15.3459C8.82996 15.6657 8.7606 15.9851 8.608 16.2669C8.4441 16.4582 8.19651 16.5566 7.946 16.5299ZM4.585 14.7679H3.939V13.2089H4.594C4.75752 13.2107 4.90863 13.2964 4.994 13.4359C5.10222 13.6145 5.15447 13.8214 5.144 14.0299C5.15654 14.2244 5.1049 14.4176 4.997 14.5799C4.89754 14.7047 4.74447 14.7746 4.585 14.7679Z"
        fill={getColor({ defaultColor: '--dark-200', color: primary })}
      />
    </svg>
  ),
  Chevron: ({ primary }: IIconColor) => (
    <svg
      width="10"
      height="17"
      viewBox="0 0 10 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.5 1.5L8.5 8.5L1.5 15.5"
        stroke={getColor({ defaultColor: '--dark-900', color: primary })}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Emergency: ({ primary }: IIconColor) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 0H2C0.9 0 0.00999999 0.9 0.00999999 2L0 16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM16 16H2V2H16V16ZM7.5 14H10.5V10.5H14V7.5H10.5V4H7.5V7.5H4V10.5H7.5V14Z"
        fill={getColor({ defaultColor: '--info-900', color: primary })}
      />
    </svg>
  ),
  Telephone: ({ primary, secondary }: IIconColor) => (
    <svg
      width="21"
      height="23"
      viewBox="0 0 21 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Telephone</title>
      <g clipPath="url(#clip0_1203_40760)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M18.8332 15.5811V18.3311C18.8351 18.8473 18.6391 19.3404 18.2933 19.6894C17.9475 20.0384 17.4839 20.2109 17.0165 20.1644C14.4522 19.8579 11.989 18.894 9.82487 17.3502C7.81139 15.9428 6.10432 14.065 4.82487 11.8502C3.41652 9.45882 2.54007 6.73613 2.26654 3.90272C2.22443 3.39019 2.3801 2.88179 2.69551 2.50171C3.01092 2.12163 3.45703 1.90487 3.92487 1.90439H6.42487C7.26142 1.89533 7.97445 2.56986 8.09154 3.48105C8.19705 4.36111 8.39274 5.22522 8.67487 6.05689C8.90382 6.72686 8.75738 7.48213 8.29987 7.99105L7.24154 9.15522C8.42783 11.4501 10.1552 13.3503 12.2415 14.6552L13.2999 13.4911C13.7625 12.9878 14.4491 12.8267 15.0582 13.0786C15.8143 13.3889 16.5998 13.6041 17.3999 13.7202C18.2378 13.8503 18.8542 14.6504 18.8332 15.5811Z"
          fill={getColor({ defaultColor: '#212529', color: primary })}
          stroke={getColor({ defaultColor: '#F5F5F5', color: secondary })}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_1203_40760">
          <rect
            width="20"
            height="22"
            fill="white"
            transform="translate(0.5 0.0703125)"
          />
        </clipPath>
      </defs>
    </svg>
  ),
  Email: ({ primary, secondary }: IIconColor) => (
    <svg
      width="21"
      height="22"
      viewBox="0 0 21 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Email</title>
      <g clipPath="url(#clip0_1203_40764)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M3.83366 3.57031H17.167C18.0837 3.57031 18.8337 4.35781 18.8337 5.32031V15.8203C18.8337 16.7828 18.0837 17.5703 17.167 17.5703H3.83366C2.91699 17.5703 2.16699 16.7828 2.16699 15.8203V5.32031C2.16699 4.35781 2.91699 3.57031 3.83366 3.57031Z"
          fill={getColor({ defaultColor: '#212529', color: primary })}
          stroke={getColor({ defaultColor: '#F5F5F5', color: secondary })}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M18.8337 5.32031L10.5003 11.4453L2.16699 5.32031"
          fill={getColor({ defaultColor: '#212529', color: primary })}
        />
        <path
          d="M18.8337 5.32031L10.5003 11.4453L2.16699 5.32031"
          stroke={getColor({ defaultColor: '#F5F5F5', color: secondary })}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_1203_40764">
          <rect
            width="20"
            height="21"
            fill="white"
            transform="translate(0.5 0.0703125)"
          />
        </clipPath>
      </defs>
    </svg>
  ),
  Archive: ({ primary }: IIconColor) => (
    <svg
      width="14"
      height="12"
      viewBox="0 0 14 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.19998 0.399902C1.31632 0.399902 0.599976 1.11625 0.599976 1.9999C0.599976 2.88356 1.31632 3.5999 2.19998 3.5999H11.8C12.6836 3.5999 13.4 2.88356 13.4 1.9999C13.4 1.11625 12.6836 0.399902 11.8 0.399902H2.19998Z"
        fill={getColor({ defaultColor: '#fff', color: primary })}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.39998 4.3999H12.6V9.9999C12.6 10.8836 11.8836 11.5999 11 11.5999H2.99998C2.11632 11.5999 1.39998 10.8836 1.39998 9.9999V4.3999ZM5.39998 6.7999C5.39998 6.35808 5.75815 5.9999 6.19998 5.9999H7.79998C8.2418 5.9999 8.59998 6.35808 8.59998 6.7999C8.59998 7.24173 8.2418 7.5999 7.79998 7.5999H6.19998C5.75815 7.5999 5.39998 7.24173 5.39998 6.7999Z"
        fill={getColor({ defaultColor: '#fff', color: primary })}
      />
    </svg>
  ),
  Edit: ({ primary }: IIconColor) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_220_12406)">
        <path
          d="M7.33331 2.66665H2.66665C2.31302 2.66665 1.97389 2.80713 1.72384 3.05718C1.47379 3.30723 1.33331 3.64637 1.33331 3.99999V13.3333C1.33331 13.6869 1.47379 14.0261 1.72384 14.2761C1.97389 14.5262 2.31302 14.6667 2.66665 14.6667H12C12.3536 14.6667 12.6927 14.5262 12.9428 14.2761C13.1928 14.0261 13.3333 13.6869 13.3333 13.3333V8.66666M12.3333 1.66665C12.5985 1.40144 12.9582 1.25244 13.3333 1.25244C13.7084 1.25244 14.0681 1.40144 14.3333 1.66665C14.5985 1.93187 14.7475 2.29158 14.7475 2.66665C14.7475 3.04173 14.5985 3.40144 14.3333 3.66665L7.99998 9.99999L5.33331 10.6667L5.99998 7.99999L12.3333 1.66665Z"
          stroke={getColor({ defaultColor: '#fff', color: primary })}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_220_12406">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),
  CaretWithCircle: ({ primary }: IIconColor) => (
    <svg width="24" height="24" fill="none">
      <circle
        cx="11.7"
        cy="11.5"
        r="11"
        stroke={getColor({ defaultColor: '#9A9AAD', color: primary })}
        strokeWidth="1.1"
      />
      <path
        stroke={getColor({ defaultColor: '#9A9AAD', color: primary })}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
        d="M13 8.3 9.2 12l3.6 3.7"
      />
    </svg>
  ),
  ShareWithBox: ({ primary }: IIconColor) => (
    <svg width="24" height="24" fill="none">
      <path
        d="M21.752 9.85333L17.0019 14.3389C16.5012 14.8118 15.6667 14.4612 15.6667 13.7634V11.3896C10.8977 11.4216 8.88584 12.5479 10.2314 17.0419C10.3793 17.5358 9.80764 17.9182 9.40653 17.6135C8.12116 16.6372 6.95833 14.7696 6.95833 12.8845C6.95833 8.13653 10.8375 7.19435 15.6667 7.16756V4.79207C15.6667 4.09362 16.5018 3.74417 17.0019 4.21653L21.752 8.70224C22.0825 9.01439 22.0828 9.54092 21.752 9.85333ZM15.6667 16.506V18.7778H5.11111V8.22223H6.79063C6.84394 8.2222 6.89668 8.21139 6.9457 8.19045C6.99473 8.16952 7.03901 8.13889 7.0759 8.10041C7.56914 7.58688 8.13927 7.18043 8.75865 6.85763C9.12578 6.66627 8.98975 6.11112 8.57574 6.11112H4.58333C3.70887 6.11112 3 6.81999 3 7.69445V19.3056C3 20.18 3.70887 20.8889 4.58333 20.8889H16.1944C17.0689 20.8889 17.7778 20.18 17.7778 19.3056V16.3762C17.7778 16.1028 17.5074 15.9122 17.2496 16.0034C16.8883 16.1312 16.5013 16.1694 16.1219 16.1148C15.8821 16.0803 15.6667 16.2637 15.6667 16.506Z"
        fill={getColor({ defaultColor: '#9A9AAD', color: primary })}
      />
    </svg>
  ),

  PiggyBank: ({ primary }: IIconColor) => (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill={getColor({ defaultColor: '#9A9AAD', color: primary })}
        d="M37.9444 17.7784H35.9981C35.4176 16.4589 34.5731 15.2911 33.5307 14.3147L34.7776 9.33358H32.6664C30.7268 9.33358 29.0114 10.2242 27.8503 11.5965C27.3488 11.5239 26.854 11.4448 26.3328 11.4448H17.8881C12.7816 11.4448 8.52624 15.0734 7.54321 19.8895H4.6931C3.71667 19.8895 2.94477 18.9989 3.14269 17.9895C3.28784 17.2374 3.99377 16.7228 4.75908 16.7228H4.82505C5.04277 16.7228 5.2209 16.5446 5.2209 16.3269V15.0074C5.2209 14.7897 5.04277 14.6116 4.82505 14.6116C2.94477 14.6116 1.26901 15.9575 1.0315 17.8179C0.741211 20.0743 2.49614 22.0007 4.6931 22.0007H7.33209C7.33209 25.4446 9.00785 28.4729 11.5545 30.3993V35.7235C11.5545 36.3041 12.0295 36.7791 12.6101 36.7791H16.8325C17.413 36.7791 17.8881 36.3041 17.8881 35.7235V32.5567H26.3328V35.7235C26.3328 36.3041 26.8079 36.7791 27.3884 36.7791H31.6108C32.1914 36.7791 32.6664 36.3041 32.6664 35.7235V30.3993C33.4449 29.8122 34.1377 29.1194 34.7314 28.3343H37.9444C38.525 28.3343 39 27.8593 39 27.2787V18.834C39 18.2534 38.525 17.7784 37.9444 17.7784ZM29.4996 22.0007C28.919 22.0007 28.444 21.5257 28.444 20.9451C28.444 20.3646 28.919 19.8895 29.4996 19.8895C30.0802 19.8895 30.5552 20.3646 30.5552 20.9451C30.5552 21.5257 30.0802 22.0007 29.4996 22.0007ZM17.8881 9.33358H26.3328C26.6891 9.33358 27.0388 9.35997 27.3818 9.38636C27.3818 9.36657 27.3884 9.35337 27.3884 9.33358C27.3884 5.83692 24.5515 3 21.0549 3C17.5582 3 14.7213 5.83692 14.7213 9.33358C14.7213 9.47213 14.7543 9.60408 14.7609 9.74262C15.7637 9.48532 16.8061 9.33358 17.8881 9.33358Z"
      />
    </svg>
  ),

  PopVakuutus: () => (
    <svg
      width="192"
      height="122"
      viewBox="0 0 192 122"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <path
          d="M27.3884 1.89038C46.4184 1.89038 57.0795 10.864 57.0795 26.8728C57.0795 42.8815 46.4184 51.8605 27.3884 51.8605H21.5054V64.8156H-0.00793457V1.89038H27.3884ZM27.3884 33.9078C32.355 33.9078 35.2022 31.4104 35.2022 26.8728C35.2022 22.3351 32.355 19.8431 27.3884 19.8431H21.5054V33.9078H27.3884Z"
          fill="#41D87E"
        />
        <path
          d="M128.902 33.3463C128.902 52.1306 115.205 66.1954 94.6162 66.1954C74.0271 66.1954 60.3276 52.1386 60.3276 33.3463C60.3276 14.5541 74.0245 0.491943 94.6162 0.491943C115.208 0.491943 128.902 14.5674 128.902 33.3463ZM107.025 33.3463C107.025 26.6803 102.058 20.7576 94.6162 20.7576C87.1742 20.7576 82.2049 26.6883 82.2049 33.3463C82.2049 40.0044 87.1689 45.9298 94.6162 45.9298C102.064 45.9298 107.025 40.0151 107.025 33.3463Z"
          fill="#41D87E"
        />
        <path
          d="M162.306 1.89038C181.336 1.89038 191.997 10.864 191.997 26.8728C191.997 42.8815 181.336 51.8605 162.306 51.8605H156.423V64.8156H134.91V1.89038H162.306ZM162.306 33.9078C167.273 33.9078 170.12 31.4104 170.12 26.8728C170.12 22.3351 167.273 19.8431 162.306 19.8431H156.423V33.9078H162.306Z"
          fill="#41D87E"
        />
        <path
          d="M49.7012 103.411L52.6334 103.256L50.9495 99.3065L49.7012 103.411Z"
          fill="#41D87E"
        />
        <path
          d="M11.3861 85.4717L13.2586 121.5L177.838 112.831L175.979 76.8002L11.3861 85.4717Z"
          fill="#41D87E"
        />
        <path
          d="M42.5805 95.0684L37.5102 109.508L33.0801 109.74L26.5411 95.916L31.1147 95.6753L35.0136 104.186L38.0281 95.3117L42.5805 95.0684Z"
          fill="white"
        />
        <path
          d="M54.0171 106.537L48.6626 106.82L48.0491 108.86L43.6402 109.093L48.6865 94.7475L52.7263 94.5336L59.2493 108.269L54.859 108.502L54.0171 106.537ZM52.6333 103.256L50.9468 99.3065L49.7011 103.403L52.6333 103.256Z"
          fill="white"
        />
        <path
          d="M68.1469 103.079L66.4338 105.325L66.5666 107.884L62.4445 108.101L61.7141 94.0603L65.8362 93.8437L66.1389 99.6461L70.6992 93.587L75.5597 93.3196L70.7072 99.7049L76.6168 107.344L71.5518 107.612L68.1469 103.079Z"
          fill="white"
        />
        <path
          d="M93.089 99.6755C93.328 104.28 90.9642 106.895 86.7306 107.112C82.497 107.328 79.8729 104.989 79.6339 100.384L79.2567 93.1351L83.4425 92.9158L83.817 100.309C83.9179 102.229 84.9405 103.315 86.5208 103.229C88.1011 103.144 89.0041 101.962 88.9112 100.039L88.5287 92.6485L92.7118 92.4265L93.089 99.6755Z"
          fill="white"
        />
        <path
          d="M111.057 98.729C111.296 103.333 108.932 105.949 104.698 106.165C100.465 106.382 97.8406 104.042 97.6015 99.4376L97.2244 92.1886L101.41 91.9693L101.795 99.3627C101.894 101.283 102.916 102.365 104.496 102.283C106.077 102.2 106.98 101.015 106.887 99.0926L106.502 91.7019L110.687 91.48L111.057 98.729Z"
          fill="white"
        />
        <path
          d="M127.258 94.2313L123.115 94.4479L123.646 104.876L119.503 105.095L118.971 94.6672L114.828 94.8865L114.64 91.2713L127.07 90.6162L127.258 94.2313Z"
          fill="white"
        />
        <path
          d="M144.862 96.9481C145.101 101.553 142.737 104.168 138.501 104.384C134.265 104.601 131.646 102.261 131.407 97.6567L131.03 90.4077L135.213 90.1884L135.598 97.5818C135.699 99.5017 136.721 100.585 138.299 100.502C139.877 100.419 140.785 99.2343 140.689 97.3117L140.307 89.921L144.49 89.6991L144.862 96.9481Z"
          fill="white"
        />
        <path
          d="M161.331 93.2849L157.167 93.5042C156.939 92.6886 156.434 92.0522 155.223 92.1164C154.322 92.1645 153.746 92.587 153.781 93.2261C153.815 93.8652 154.237 94.0924 155.109 94.2342L157.43 94.6085C159.82 95.0016 161.594 95.9615 161.725 98.483C161.876 101.395 159.6 103.275 155.855 103.47C153.045 103.62 149.704 102.636 149.069 98.7344L153.295 98.5124C153.56 99.5339 154.407 100.117 155.738 100.061C156.684 100.01 157.217 99.6087 157.188 99.0312C157.167 98.6381 156.984 98.2557 156.049 98.098L153.682 97.6648C150.899 97.1674 149.507 96.1032 149.39 93.8518C149.236 90.8784 151.515 89.0387 154.891 88.8623C157.085 88.7473 160.755 89.3195 161.331 93.2849Z"
          fill="white"
        />
      </g>
    </svg>
  ),
  badgeCheck: ({ primary }: IIconColor) => (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.52032 4.64597C8.292 4.58439 9.02459 4.28094 9.61379 3.77883C10.9888 2.60709 13.011 2.60709 14.386 3.77883C14.9752 4.28094 15.7078 4.58439 16.4795 4.64597C18.2803 4.78968 19.7103 6.21965 19.854 8.02044C19.9155 8.79212 20.219 9.52471 20.7211 10.1139C21.8928 11.4889 21.8928 13.5112 20.7211 14.8861C20.219 15.4753 19.9155 16.2079 19.854 16.9796C19.7103 18.7804 18.2803 20.2104 16.4795 20.3541C15.7078 20.4157 14.9752 20.7191 14.386 21.2212C13.011 22.393 10.9888 22.393 9.61379 21.2212C9.02459 20.7191 8.292 20.4157 7.52032 20.3541C5.71953 20.2104 4.28955 18.7804 4.14585 16.9796C4.08427 16.2079 3.78082 15.4753 3.2787 14.8861C2.10697 13.5112 2.10697 11.4889 3.2787 10.1139C3.78082 9.52471 4.08427 8.79212 4.14585 8.02044C4.28955 6.21965 5.71953 4.78968 7.52032 4.64597ZM16.4484 10.9486C16.9171 10.4799 16.9171 9.72013 16.4484 9.2515C15.9798 8.78287 15.22 8.78287 14.7514 9.2515L10.7999 13.203L9.24843 11.6515C8.7798 11.1829 8.02 11.1829 7.55137 11.6515C7.08275 12.1201 7.08275 12.8799 7.55137 13.3486L9.95137 15.7486C10.42 16.2172 11.1798 16.2172 11.6484 15.7486L16.4484 10.9486Z"
        fill={getColor({ defaultColor: '#326196', color: primary })}
      />
    </svg>
  ),
  puzzlePiece: ({ primary }: IIconColor) => (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.5001 3.67559C8.5001 3.01284 9.03736 2.47559 9.7001 2.47559C10.3628 2.47559 10.9001 3.01284 10.9001 3.67559V4.07559C10.9001 4.51741 11.2583 4.87559 11.7001 4.87559H14.1001C14.5419 4.87559 14.9001 5.23376 14.9001 5.67559V8.07559C14.9001 8.51741 14.5419 8.87559 14.1001 8.87559H13.7001C13.0374 8.87559 12.5001 9.41284 12.5001 10.0756C12.5001 10.7383 13.0374 11.2756 13.7001 11.2756H14.1001C14.5419 11.2756 14.9001 11.6338 14.9001 12.0756V14.4756C14.9001 14.9174 14.5419 15.2756 14.1001 15.2756H11.7001C11.2583 15.2756 10.9001 14.9174 10.9001 14.4756V14.0756C10.9001 13.4128 10.3628 12.8756 9.7001 12.8756C9.03736 12.8756 8.5001 13.4128 8.5001 14.0756V14.4756C8.5001 14.9174 8.14192 15.2756 7.7001 15.2756H5.3001C4.85827 15.2756 4.5001 14.9174 4.5001 14.4756V12.0756C4.5001 11.6338 4.14193 11.2756 3.7001 11.2756H3.3001C2.63736 11.2756 2.1001 10.7383 2.1001 10.0756C2.1001 9.41284 2.63736 8.87559 3.3001 8.87559H3.7001C4.14193 8.87559 4.5001 8.51741 4.5001 8.07559V5.67559C4.5001 5.23376 4.85827 4.87559 5.3001 4.87559H7.7001C8.14192 4.87559 8.5001 4.51741 8.5001 4.07559V3.67559Z"
        fill="#326196"
      />
    </svg>
  ),
  Fire: ({ primary, secondary }: IIconColor) => (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        stroke={getColor({ defaultColor: '--light-100', color: primary })}
        fill={getColor({ defaultColor: '--danger-500', color: secondary })}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.6081 2.44023C14.9408 2.52858 15.2195 2.75551 15.3734 3.06337C15.9537 4.22392 16.3868 4.92829 16.8225 5.49938C17.2635 6.07746 17.73 6.55069 18.4397 7.26036C20.0797 8.90033 20.9 11.0522 20.9 13.2001C20.9 15.348 20.0796 17.4998 18.4397 19.1398C15.1593 22.4202 9.84069 22.4202 6.56028 19.1398C4.92031 17.4998 4.09998 15.348 4.09998 13.2001C4.09997 11.0522 4.92031 8.90033 6.56028 7.26036C6.90348 6.91716 7.41962 6.81449 7.86803 7.00023C8.31644 7.18597 8.60881 7.62353 8.60881 8.10889C8.60881 9.45266 8.69272 10.4766 9.08575 11.2933C9.30187 11.7423 9.639 12.1814 10.22 12.5732C10.3588 11.3003 10.613 9.7494 10.9565 8.27735C11.2269 7.11837 11.5633 5.9613 11.9636 4.99642C12.1638 4.51381 12.3902 4.05427 12.6474 3.65751C12.8977 3.27132 13.2193 2.87832 13.6345 2.60157C13.9208 2.41064 14.2754 2.35189 14.6081 2.44023ZM15.0456 18.1456C13.6397 19.5515 11.3603 19.5515 9.95439 18.1456C9.25145 17.4427 8.89998 16.5214 8.89998 15.6C8.89998 15.6 9.95439 16.2 11.9 16.2C11.9 15 12.5 11.4 13.4 10.8C14 12 14.3426 12.3515 15.0456 13.0544C15.7485 13.7574 16.1 14.6787 16.1 15.6C16.1 16.5214 15.7485 17.4427 15.0456 18.1456Z"
      />
    </svg>
  ),

  Storebrand: () => (
    <svg
      width="126"
      height="18"
      viewBox="0 0 126 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M123.512 3.12229V7.32695C123.149 6.81877 122.424 6.31138 121.192 6.31138C119.089 6.31138 117.857 7.90593 117.857 10.6624C117.857 12.3293 118.219 13.562 118.945 14.2865C119.452 14.7938 120.249 15.0841 121.192 15.0841C122.496 15.0841 123.149 14.3588 123.512 13.7799V14.9402H125.976V4.13707L123.512 3.12229ZM121.845 13.4172C121.482 13.4172 121.192 13.3448 120.902 13.0546C120.467 12.6195 120.323 11.8227 120.323 10.6624C120.323 9.79153 120.323 8.77754 120.902 8.19699C121.12 7.97987 121.482 7.83434 121.845 7.83434C122.28 7.83434 122.57 7.97987 122.859 8.19699C123.294 8.70438 123.512 9.50283 123.512 10.6624C123.512 11.9674 123.078 13.4172 121.845 13.4172ZM50.8662 6.45691V3.12229L48.4732 4.13707V12.7643C48.4732 13.2717 48.5463 13.9246 49.0537 14.4312C49.4887 14.8662 50.2864 15.0841 51.5183 15.0841C52.0981 15.0841 52.6786 15.0841 53.3307 14.9394H53.4031V13.4164H53.3307C52.9689 13.4888 52.6778 13.5612 52.316 13.5612C51.8078 13.5612 51.5183 13.4888 51.3004 13.2709C50.9377 12.9082 50.8654 12.4732 50.8654 11.8219V7.97908H53.5479V6.45691H50.8662ZM62.3214 7.47169C61.5237 6.67403 60.5089 6.31138 59.1315 6.31138C56.3767 6.31138 54.7813 7.90592 54.7813 10.7347C54.7813 12.1114 55.2163 13.1993 55.9416 13.9962C56.7393 14.7215 57.7541 15.0841 59.1323 15.0841C60.5097 15.0841 61.5245 14.7215 62.3222 13.9962C63.0467 13.1985 63.4093 12.1114 63.4093 10.7347C63.4085 9.28493 63.0459 8.19698 62.3214 7.47169ZM57.9712 8.26936C58.2615 7.97987 58.6233 7.83434 59.1315 7.83434C59.6389 7.83434 60.0008 7.97987 60.291 8.26936C60.7984 8.77753 60.8708 9.86548 60.8708 10.7347C60.8708 12.1853 60.6537 13.562 59.1315 13.562C58.6233 13.562 58.2615 13.4172 57.9712 13.1993C57.463 12.6203 57.3914 11.6048 57.3914 10.7347C57.3914 9.86469 57.4638 8.77753 57.9712 8.26936ZM78.7789 7.32695C78.126 6.67403 77.1844 6.31138 75.9517 6.31138C73.2692 6.31138 71.7463 7.68881 71.7463 10.2997C71.7463 11.8227 72.1813 12.9822 72.9782 13.8522C73.8474 14.6499 75.0817 15.0841 76.6762 15.0841C77.6902 15.0841 78.561 15.0118 79.3587 14.7939H79.4311V13.2709H79.2848C78.7247 13.4618 78.1372 13.5599 77.5455 13.5612C76.4575 13.5612 75.6606 13.2709 74.9354 12.6195C74.3564 11.9666 74.0661 11.169 73.9937 10.9511H79.6482V9.86469C79.6482 8.84991 79.3595 7.90671 78.7789 7.32695ZM75.8062 7.76197C76.3144 7.76197 76.7494 7.83434 76.9665 8.12461C77.2568 8.41489 77.4747 8.84991 77.4747 9.64758H74.0669C74.1393 8.77754 74.4295 7.76197 75.8062 7.76197ZM88.7844 7.18221C88.2038 6.60166 87.4801 6.31217 86.5369 6.31217C85.5213 6.31217 84.6521 6.67482 84.0723 7.40011V3.12229L81.6785 4.13707V11.3145C81.6785 12.4748 81.9688 13.418 82.6217 14.0693C83.347 14.7946 84.3626 15.0849 85.7392 15.0849C87.0435 15.0849 88.0591 14.7946 88.7844 14.0693C89.5097 13.3456 89.8723 12.1853 89.8723 10.7355C89.8723 9.06781 89.5097 7.90671 88.7844 7.18221ZM85.7392 13.562C85.2318 13.562 84.87 13.4896 84.6513 13.1993C84.2163 12.8367 84.0723 12.039 84.0723 10.7347C84.0723 9.57441 84.3626 8.55885 84.87 8.12461C85.0863 7.83434 85.3774 7.76197 85.7392 7.76197C86.1743 7.76197 86.4645 7.83434 86.6824 8.05225C87.2614 8.63201 87.3338 9.71994 87.3338 10.7355C87.3338 12.6203 86.8272 13.562 85.7392 13.562ZM115.174 7.03746C114.667 6.52929 114.014 6.31217 113.217 6.31217C111.622 6.31217 110.825 7.25458 110.39 7.90671V6.45691H107.998V14.8662H110.39V11.2421C110.39 10.5168 110.68 9.50283 111.331 8.77754C111.694 8.48726 112.131 8.26936 112.565 8.26936C112.928 8.26936 113.072 8.34173 113.216 8.41489C113.363 8.63122 113.506 9.06781 113.506 10.0818V14.8662H115.899V9.06781C115.899 8.19699 115.608 7.54485 115.174 7.03746ZM104.952 7.18221C104.372 6.60166 103.429 6.31217 102.051 6.31217C101.472 6.31217 100.312 6.31217 99.3688 6.60244H99.2964V8.41489L99.4427 8.34173C100.675 7.68881 101.98 7.76197 102.051 7.76197C102.486 7.76197 102.777 7.90671 102.994 8.12461C103.211 8.34173 103.285 8.70438 103.285 9.21256V9.64758H103.211C101.834 9.64758 98.0637 9.79232 98.0637 12.4748C98.0637 13.1277 98.354 13.853 98.8614 14.3596C99.3688 14.8662 100.093 15.0849 100.891 15.0849C102.269 15.0849 102.922 14.432 103.285 13.9246V14.9402H105.605V8.92228C105.605 8.19699 105.387 7.61722 104.952 7.18221ZM101.254 13.4172C100.528 13.4172 100.093 12.9822 100.093 12.474C100.093 12.1853 100.166 11.895 100.384 11.6763C101.036 11.025 102.559 10.9511 103.285 10.9511C103.285 11.5316 103.211 12.4016 102.632 12.9822C102.269 13.2717 101.835 13.4172 101.254 13.4172ZM97.0489 6.31217C95.4544 6.31217 94.6567 7.25458 94.2941 7.90671V6.45691H91.9019V14.8662H94.2941V11.2421C94.2941 10.5168 94.512 9.50283 95.2373 8.77754C95.526 8.48726 95.961 8.26936 96.4692 8.26936H96.6139C96.7602 8.26936 96.9042 8.26936 97.0489 8.34173H97.1229V6.31138L97.0489 6.31217ZM70.5144 6.31217C68.9914 6.31217 68.1945 7.25458 67.7579 7.90671V6.45691H65.3657V14.8662H67.7579V11.2421C67.7579 10.5168 68.0482 9.50283 68.7011 8.77754C69.0638 8.48726 69.4988 8.26936 69.933 8.26936H70.5136L70.6583 8.41489V6.31217H70.5144ZM46.516 11.6771C46.5199 12.0682 46.4449 12.4561 46.2954 12.8176C46.1459 13.179 45.925 13.5066 45.6459 13.7806C44.7759 14.6499 43.3261 15.0849 41.3681 15.0849C39.8452 15.0849 38.4677 14.7946 37.888 14.6499H37.6709L37.5253 14.5775V11.9674L37.8156 12.1853L37.888 12.2577C38.6856 12.6927 39.9183 13.2717 41.4405 13.2717C42.2382 13.2717 42.8903 13.1269 43.1806 12.7643C43.5432 12.474 43.5432 12.1114 43.5432 11.9666C43.5432 10.9511 42.2382 10.2997 40.8607 9.64679C39.193 8.84912 37.2358 7.97908 37.2358 6.09426C37.2358 5.36897 37.5261 4.71684 38.0335 4.20945C38.8312 3.41178 40.281 2.90439 42.021 2.90439C43.4708 2.90439 44.7035 3.1223 45.2117 3.26704H45.5012V5.58688L44.9214 5.36976C44.1238 5.08027 43.109 4.79 41.9487 4.79C41.2957 4.79 40.7891 4.93474 40.4989 5.22502C40.2818 5.36976 40.2086 5.66003 40.2086 5.95031C40.2086 6.67481 41.2958 7.18299 42.6016 7.76275C44.4133 8.63122 46.516 9.5752 46.516 11.6771Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M32.4685 9.08414V9.01099C32.4685 7.12617 31.6708 4.73396 30.2934 3.35653C28.7518 1.82444 26.6668 0.96421 24.4934 0.963533C22.5362 0.963533 20.5782 1.68883 19.1284 2.92151C18.2584 2.77598 17.316 2.63124 16.3012 2.63124C15.3588 2.63124 14.4164 2.77676 13.5463 2.92151C12.0474 1.65218 10.1455 0.958067 8.18138 0.963533C3.83119 0.963533 0.206299 4.8787 0.206299 9.0102V9.51916C0.206299 11.404 1.00396 13.288 2.38139 14.6662C3.90356 16.1892 6.00629 16.9861 8.18138 16.9861C10.6467 16.9861 12.8211 16.0429 14.344 14.3036C14.9961 14.376 15.6491 14.376 16.3012 14.376C17.0257 14.376 17.6786 14.376 18.3316 14.3036C19.8545 16.0429 22.1012 16.9861 24.4942 16.9861C26.6693 16.9861 28.772 16.1884 30.2942 14.6662C31.6716 13.288 32.4693 11.4048 32.4693 9.51916L32.4685 9.08414ZM5.42574 10.8242C4.94784 10.3491 4.62012 9.744 4.48333 9.08414C4.55649 8.35964 4.91835 7.70672 5.42574 7.19933C6.04268 6.56914 6.86653 6.18306 7.74557 6.11217V9.59232C7.74557 10.39 8.10822 11.1869 8.76114 11.9122C8.54403 11.9122 8.39849 11.9845 8.18138 11.9845C7.16581 11.9845 6.15103 11.5495 5.42574 10.8242ZM24.4934 11.9845C24.2763 11.9845 24.0584 11.9122 23.9136 11.9122C24.5666 11.1869 24.856 10.3892 24.856 9.51995V6.11138C25.7985 6.18376 26.5961 6.54641 27.2483 7.19854C27.7564 7.70672 28.1183 8.35885 28.1907 9.08336C27.9012 10.6803 26.3782 11.9845 24.4934 11.9845Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.1803 13.5791C5.7873 13.5791 3.83011 11.7674 3.83011 9.51994C3.83011 7.27169 5.7873 5.45925 8.1803 5.45925C9.848 5.45925 11.2254 6.32928 11.9499 7.56197C12.6029 8.28647 13.9803 8.79465 15.5748 8.86702C15.213 5.38687 12.0223 2.63202 8.1803 2.63202C4.12038 2.63202 0.785767 5.74952 0.785767 9.51994C0.785767 13.3627 4.12038 16.4079 8.1803 16.4079C10.3554 16.4079 12.3126 15.537 13.69 14.1604C12.385 13.9425 11.2254 13.5799 10.2822 13.0725C9.63088 13.3627 8.97875 13.5791 8.1803 13.5791ZM24.4931 2.63202C20.6503 2.63202 17.4604 5.38687 17.0978 8.86702C18.6931 8.79465 20.0705 8.28647 20.7227 7.56197C21.4472 6.32928 22.8978 5.45925 24.4923 5.45925C26.8845 5.45925 28.8425 7.27169 28.8425 9.51994C28.8425 11.7674 26.8845 13.5791 24.4923 13.5791C23.7616 13.575 23.0418 13.4013 22.3896 13.0717C21.4472 13.5791 20.2869 13.9409 18.9818 14.1596C20.3592 15.5363 22.3172 16.4071 24.4915 16.4071C28.5514 16.4071 31.8868 13.362 31.8868 9.51916C31.8884 5.74873 28.553 2.63202 24.4931 2.63202Z"
        fill="#C9322D"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M24.2761 9.51995V7.2717H24.2037C23.6963 9.22968 20.3609 10.7518 16.301 10.7518C12.3135 10.7518 8.97885 9.22889 8.3983 7.2717H8.32593V9.51995C8.32593 10.3176 8.76095 11.1145 9.55861 11.6943C10.7905 12.6375 12.7485 13.2888 14.996 13.5075C15.431 13.5799 15.866 13.5799 16.301 13.5799C16.8084 13.5799 17.2434 13.5799 17.6784 13.5075C19.9259 13.2896 21.8839 12.6382 23.0434 11.6943C23.8411 11.0421 24.2761 10.3176 24.2761 9.51995Z"
        fill="#C9322D"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.3734 7.92462C16.3734 7.92462 16.4457 7.92462 16.4457 7.85225C16.736 6.62035 17.2434 5.38767 18.1134 4.44447C17.606 4.29973 16.9531 4.22736 16.301 4.22736C15.7212 4.22736 15.0683 4.29973 14.5609 4.44447C15.431 5.38688 15.9383 6.61957 16.2286 7.85225C16.301 7.92462 16.301 7.92462 16.3734 7.92462Z"
        fill="#C9322D"
      />
    </svg>
  ),
  Download: ({ primary }: IIconColor) => (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.33325 13.9296L3.33325 14.7629C3.33325 16.1437 4.45254 17.2629 5.83325 17.2629L14.1666 17.2629C15.5473 17.2629 16.6666 16.1437 16.6666 14.7629L16.6666 13.9296M13.3333 10.5963L9.99992 13.9296M9.99992 13.9296L6.66658 10.5963M9.99992 13.9296L9.99992 3.92961"
        stroke={getColor({ defaultColor: '#919191', color: primary })}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Tag: ({ primary }: IIconColor) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.1657 7.43429C14.4781 7.74671 14.4781 8.25324 14.1657 8.56566L8.56566 14.1657C8.25324 14.4781 7.74671 14.4781 7.43429 14.1657L1.83429 8.56566C1.67806 8.40943 1.59995 8.20465 1.59998 7.99988V3.99998C1.59998 2.67449 2.67449 1.59998 3.99998 1.59998H8.00019C8.20486 1.60003 8.40951 1.67814 8.56566 1.83429L14.1657 7.43429ZM3.99998 4.79998C4.4418 4.79998 4.79998 4.4418 4.79998 3.99998C4.79998 3.55815 4.4418 3.19998 3.99998 3.19998C3.55815 3.19998 3.19998 3.55815 3.19998 3.99998C3.19998 4.4418 3.55815 4.79998 3.99998 4.79998Z"
        fill={getColor({ defaultColor: '#3B7A64', color: primary })}
      />
    </svg>
  ),
  Eye: ({ primary }: IIconColor) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M10 12C11.1046 12 12 11.1046 12 10C12 8.89543 11.1046 8 10 8C8.89544 8 8.00001 8.89543 8.00001 10C8.00001 11.1046 8.89544 12 10 12Z"
        fill={getColor({ defaultColor: '#4A987D', color: primary })}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.457764 10C1.73202 5.94291 5.52232 3 9.99997 3C14.4776 3 18.2679 5.94288 19.5422 9.99996C18.2679 14.0571 14.4776 17 9.99995 17C5.52232 17 1.73204 14.0571 0.457764 10ZM14 10C14 12.2091 12.2091 14 10 14C7.79087 14 6.00001 12.2091 6.00001 10C6.00001 7.79086 7.79087 6 10 6C12.2091 6 14 7.79086 14 10Z"
        fill={getColor({ defaultColor: '#4A987D', color: primary })}
      />
    </svg>
  ),
  CircleUser: ({ primary }: IIconColor) => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.3999 7.0001C13.3999 10.5347 10.5346 13.4001 6.99996 13.4001C3.46535 13.4001 0.599976 10.5347 0.599976 7.0001C0.599976 3.46548 3.46535 0.600098 6.99996 0.600098C10.5346 0.600098 13.3999 3.46548 13.3999 7.0001ZM8.59996 4.6001C8.59996 5.48375 7.88361 6.2001 6.99996 6.2001C6.11631 6.2001 5.39996 5.48375 5.39996 4.6001C5.39996 3.71644 6.11631 3.0001 6.99996 3.0001C7.88361 3.0001 8.59996 3.71644 8.59996 4.6001ZM6.99991 7.8001C5.38588 7.8001 3.99512 8.75606 3.36296 10.1327C4.24319 11.1537 5.54608 11.8001 6.99995 11.8001C8.45378 11.8001 9.75665 11.1538 10.6369 10.1327C10.0047 8.75609 8.61396 7.8001 6.99991 7.8001Z"
        fill={getColor({ defaultColor: '#3B7A64', color: primary })}
      />
    </svg>
  ),
  User: ({ primary }: IIconColor) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M12 10.8001C13.9882 10.8001 15.6 9.18832 15.6 7.2001C15.6 5.21187 13.9882 3.6001 12 3.6001C10.0118 3.6001 8.40001 5.21187 8.40001 7.2001C8.40001 9.18832 10.0118 10.8001 12 10.8001Z"
        fill={getColor({ defaultColor: 'black', color: primary })}
      />
      <path
        d="M3.60001 21.6001C3.60001 16.9609 7.36081 13.2001 12 13.2001C16.6392 13.2001 20.4 16.9609 20.4 21.6001H3.60001Z"
        fill={getColor({ defaultColor: 'black', color: primary })}
      />
    </svg>
  ),
  Menu: ({ primary }: IIconColor) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.59998 5.9998C3.59998 5.33706 4.13723 4.7998 4.79998 4.7998H19.2C19.8627 4.7998 20.4 5.33706 20.4 5.9998C20.4 6.66255 19.8627 7.1998 19.2 7.1998H4.79998C4.13723 7.1998 3.59998 6.66255 3.59998 5.9998Z"
        fill={getColor({ defaultColor: 'black', color: primary })}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.59998 11.9998C3.59998 11.3371 4.13723 10.7998 4.79998 10.7998H19.2C19.8627 10.7998 20.4 11.3371 20.4 11.9998C20.4 12.6625 19.8627 13.1998 19.2 13.1998H4.79998C4.13723 13.1998 3.59998 12.6625 3.59998 11.9998Z"
        fill={getColor({ defaultColor: 'black', color: primary })}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.59998 17.9998C3.59998 17.3371 4.13723 16.7998 4.79998 16.7998H12C12.6627 16.7998 13.2 17.3371 13.2 17.9998C13.2 18.6625 12.6627 19.1998 12 19.1998H4.79998C4.13723 19.1998 3.59998 18.6625 3.59998 17.9998Z"
        fill={getColor({ defaultColor: 'black', color: primary })}
      />
    </svg>
  ),

  Trash: ({ primary }: IIconColor) => (
    <svg
      width="14"
      height="16"
      viewBox="0 0 14 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill={getColor({ defaultColor: 'black', color: primary })}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6 0C5.62123 0 5.27497 0.214002 5.10557 0.552786L4.38197 2H1C0.447715 2 0 2.44772 0 3C0 3.55228 0.447716 4 1 4L1 14C1 15.1046 1.89543 16 3 16H11C12.1046 16 13 15.1046 13 14V4C13.5523 4 14 3.55228 14 3C14 2.44772 13.5523 2 13 2H9.61804L8.89443 0.552786C8.72504 0.214002 8.37877 0 8 0H6ZM4 6C4 5.44772 4.44772 5 5 5C5.55228 5 6 5.44772 6 6V12C6 12.5523 5.55228 13 5 13C4.44772 13 4 12.5523 4 12V6ZM9 5C8.44771 5 8 5.44772 8 6V12C8 12.5523 8.44771 13 9 13C9.55229 13 10 12.5523 10 12V6C10 5.44772 9.55229 5 9 5Z"
      />
    </svg>
  ),
});
